import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { ScrollArea } from "./ui/scroll-area";
import { aiServiceSelector } from '../services/aiServiceSelector';
import aiService from '../services/aiService';
import engagementService from '../services/engagementService';
import { userService } from '../services/userService';
import { renderAgentCards } from './agents/AgentCards';
import QuickActionScheduleDialog from './agents/QuickActionScheduleDialog';
import FinancialIntakeDialog from './agents/FinancialIntakeDialog';
import {
  QuickActionPreferences,
  QuickActionHistory,
  QuickActionReport,
  QuickActionReports,
  DEFAULT_QUICK_ACTION_PREFERENCES,
  DEFAULT_QUICK_ACTION_HISTORY,
  DEFAULT_QUICK_ACTION_REPORTS,
  QUICK_ACTION_METADATA,
  QuickActionType,
  RESEARCH_HIERARCHY,
  AI_STRUCTURED_FORMATS,
  AIStructuredFormat,
  QuickActionReportStatus,
} from "../utils/financialTaxonomy";
import {
  handleGeneratePortfolioReport,
  handleScheduleMeeting,
  handleCreateInvestmentProposal,
  handleRunRiskAnalysis,
  handleReviewFinancialGoals,
  handleGetMarketInsights,
  downloadPDFReport,
  //type QuickActionReports,
  type ClientContext
} from '../utils/quickActionHandlers';
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import {
  Send,
  Sparkles,
  TrendingUp,
  FileText,
  Calendar,
  Users,
  Brain,
  Target,
  AlertCircle,
  CheckCircle,
  Clock,
  Zap,
  Loader2,
  MessageSquare,
  BarChart3,
  Upload,
  Search,
  File,
  Download,
  Eye,
  Trash2,
  UserCircle,
  Settings,
  XCircle,
  ChevronDown,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { portfolioService, investorService } from "@/services";

/**
 * Detects missing data in an agent's structured output so the chat can prompt
 * the user to provide it interactively.
 */
function detectMissingData(domain: string, data: any) {
  if (!data) return [];
  const gaps: { key: string; label: string; prompt: string }[] = [];
  const missing = (v: any) => v == null || v === 0 || v === '';
  const allZero = (obj: any) => !obj || Object.values(obj).every((v: any) => missing(v));

  if (domain === 'financial-planning') {
    if (allZero(data.cashFlow)) {
      gaps.push({
        key: 'cashflow',
        label: 'Add income & expenses',
        prompt: 'Please add my cash flow details so the plan is accurate: monthly income, monthly expenses, and monthly debt payments. My values are: ',
      });
    }
    if (!data.goals || data.goals.length === 0) {
      gaps.push({
        key: 'goals',
        label: 'Add savings goals',
        prompt: 'Add these goals to my financial plan: ',
      });
    }
    if (!data.actionItemsNow || data.actionItemsNow.length === 0) {
      gaps.push({
        key: 'actions',
        label: 'Generate action items',
        prompt: 'Include clear Action Items (Now) and Action Items (Future) in the plan.',
      });
    }
  } else if (domain === 'investment') {
    if (allZero(data.currentAllocation)) {
      gaps.push({
        key: 'allocation',
        label: 'Add portfolio holdings',
        prompt: 'My current holdings / asset allocation is: ',
      });
    }
  } else if (domain === 'retirement-tax') {
    if (!data.projection || (missing(data.projection.currentBalance) && missing(data.projection.projectedBalanceAtRetirement))) {
      gaps.push({
        key: 'retirement',
        label: 'Add retirement balances',
        prompt: 'My retirement account balances and expected annual income are: ',
      });
    }
  } else if (domain === 'estate-risk') {
    if (allZero(data.netWorth)) {
      gaps.push({
        key: 'networth',
        label: 'Add assets & liabilities',
        prompt: 'My assets and liabilities are: ',
      });
    }
  }
  return gaps;
}

interface Message {
  id: string;
  sender: string;
  role: "investor" | "analyst" | "advisor" | "ai";
  content: string;
  createdAt: string;
  aiGenerated?: boolean;
  structuredJson?: any;
  domain?: string;
}

interface AIInsight {
  id: string;
  type: "recommendation" | "alert" | "opportunity" | "analysis";
  userId: string;
  clientId: string;
  title: string;
  description: string;
  details: string;
  confidence: number;
  relevantTo: string[];
  createdAt: string;
  aiSuggested?: boolean;
}

interface Task {
  id: string;
  userId: string;
  clientId: string;
  title: string;
  assignedTo: string;
  priority: "high" | "medium" | "low";
  status: "pending" | "in-progress" | "completed" | "cancelled";
  aiSuggested: boolean;
  comment?: string;
  createdAt?: string;
}

interface UploadedDocument {
  id: string;
  name: string;
  type: string;
  size: string;
  uploadedBy: string;
  uploadDate: string;
  status: "processing" | "ready" | "failed";
}

interface DocumentResearch {
  id: string;
  documentId: string;
  documentName: string;
  type: "summary" | "insights" | "recommendations" | "risk-analysis";
  title: string;
  content: string;
  confidence: number;
  createdAt: string;
}


interface AIQuickActionWithLatestHistoryDTO {
  // Preference fields
  actionId: string;        // UUID -> string
  userId: string;          // UUID -> string
  clientId: string;        // UUID -> string
  actionType: string;
  actionName: string;
  description: string;
  parameters: Record<string, any>;  // Map<String, Object> -> Record<string, any>
  enabled: boolean;
  createdAt: string;       // LocalDateTime -> string (ISO 8601 format)
  updatedAt: string;       // LocalDateTime -> string (ISO 8601 format)
  
  // Latest History fields (can be null if no history exists)
  historyId?: string;      // UUID -> string, optional
  status?: string;         // optional
  triggerType?: string;    // optional
  reportFilename?: string; // optional
  reportUrl?: string;      // optional
  fileSizeBytes?: number;  // Long -> number, optional
  resultData?: Record<string, any>; // Map<String, Object> -> Record<string, any>, optional
  historyCreatedAt?: string; // LocalDateTime -> string (ISO 8601 format), optional
  completedAt?: string;   // LocalDateTime -> string (ISO 8601 format), optional
  processingTimeMs?: number; // Long -> number, optional
  canRun: boolean; // Rerun flag
  prompt?: string; // Custom prompt for the AI
  reportType?: string; // Report type (PDF, etc.)
}

interface CombinedQuickAction {
  type: QuickActionType;
  metadata: typeof QUICK_ACTION_METADATA[QuickActionType];
  apiData?: AIQuickActionWithLatestHistoryDTO;
  hasApiData: boolean;
}

interface AICollaborationToolProps {
  role?: "investor" | "analyst" | "advisor";
}

const AGENT_MODES = [
  { id: "general", label: "General" },
  { id: "financial-planning", label: "Financial Planning" },
  { id: "investment", label: "Investments" },
  { id: "retirement-tax", label: "Retirement & Tax" },
  { id: "estate-risk", label: "Estate & Risk" },
];

const DOMAIN_LABELS: Record<string, string> = {
  general: "General",
  "financial-planning": "Financial Plan",
  investment: "Investments",
  "retirement-tax": "Retirement & Tax",
  "estate-risk": "Estate & Risk",
};

const AGENT_PROMPTS: Record<string, string[]> = {
  "financial-planning": [
    "Create a comprehensive financial plan including cash flow, goals, budget, and Action Items (Now) and Action Items (Future).",
    "How much should I save monthly for a house in 7 years?",
    "Build a budget to improve my savings rate.",
    "Assess my cash flow and debt.",
  ],
  investment: [
    "Should I rebalance my portfolio?",
    "Is my allocation aligned with my risk tolerance?",
    "Show me my portfolio drift and proposed trades.",
  ],
  "retirement-tax": [
    "Run a 10-year Monte Carlo retirement income projection and show how much I can safely withdraw each year.",
    "Can I afford to retire in 25 years?",
    "What is the best withdrawal order for my accounts?",
    "Suggest tax-loss harvesting candidates.",
  ],
  "estate-risk": [
    "Do I have enough life insurance coverage?",
    "Assess my insurance gaps.",
    "Draft an estate plan overview.",
  ],
};

const formatDate = (dateString?: string) => {
  if (!dateString) return new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return dateString; // Fallback to raw string if invalid but not null
  }
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

const formatDateTime = (dateString?: string) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return dateString;
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export function AICollaborationTool({ role = "investor" }: AICollaborationToolProps) {
  const [selectedClient, setSelectedClient] = useState<string>("self");
  const isClientSelected = selectedClient !== "all" && selectedClient !== "self";
  const currentUserId = localStorage.getItem('userId') || '';
  const isProfessional = role?.toLowerCase() === "analyst" || role?.toLowerCase() === "advisor";
  // Self-directed investors act as their own client: clientId == userId so
  // quick action schedules/instant runs and agent chat all scope to self.
  const effectiveClientId = isProfessional
    ? (isClientSelected ? selectedClient : '')
    : currentUserId;
  const [clientList, setClientList] = useState<any[]>([
    // { id: "all", name: "All Clients", portfolio: "$12.5M", email: "", role: "system" }
  ]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const clients = clientList;

    // Load quick action preferences from localStorage (default: all disabled)
  const [quickActionPrefs, setQuickActionPrefs] = useState<QuickActionPreferences>(() => {
    const saved = localStorage.getItem('aiQuickActionPreferences');
    return saved ? JSON.parse(saved) : DEFAULT_QUICK_ACTION_PREFERENCES;
  });

  // Track which actions have been run (to auto-run on first enable)
  const [actionHistory, setActionHistory] = useState<QuickActionHistory>(() => {
    const saved = localStorage.getItem('aiQuickActionHistory');
    return saved ? JSON.parse(saved) : DEFAULT_QUICK_ACTION_HISTORY;
  });

  // Track generated reports and their status
  const [generatedReports, setGeneratedReports] = useState<QuickActionReports>(() => {
    const saved = localStorage.getItem('aiQuickActionReports');
    return saved ? JSON.parse(saved) : DEFAULT_QUICK_ACTION_REPORTS;
  });

  // Track API quick actions with history
  const [apiQuickActions, setApiQuickActions] = useState<AIQuickActionWithLatestHistoryDTO[]>([]);
  const [combinedQuickActions, setCombinedQuickActions] = useState<CombinedQuickAction[]>([]);

  // Guided chat state
  const [selectedResearchCategory, setSelectedResearchCategory] = useState<keyof typeof RESEARCH_HIERARCHY | null>(null);
  const [selectedAnalysisType, setSelectedAnalysisType] = useState<string | null>(null);
  const [showGuidedChat, setShowGuidedChat] = useState(false);
  const [isChatSessionActive, setIsChatSessionActive] = useState(false);
  const [currentSession, setCurrentSession] = useState<any>(null);
  const [sharePortfolio, setSharePortfolio] = useState(true);
  const [shareGoals, setShareGoals] = useState(true);

  // Edit Quick Action state
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingAction, setEditingAction] = useState<QuickActionType | null>(null);
  const [editPrompt, setEditPrompt] = useState("");
  const [editActionName, setEditActionName] = useState("");
  const [isSavingAction, setIsSavingAction] = useState(false);

  // Save preferences to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('aiQuickActionPreferences', JSON.stringify(quickActionPrefs));
  }, [quickActionPrefs]);

  // Save action history to localStorage
  useEffect(() => {
    localStorage.setItem('aiQuickActionHistory', JSON.stringify(actionHistory));
  }, [actionHistory]);

  // Save generated reports to localStorage
  useEffect(() => {
    localStorage.setItem('aiQuickActionReports', JSON.stringify(generatedReports));
  }, [generatedReports]);

  const markActionAsRun = (action: keyof QuickActionHistory) => {
    setActionHistory(prev => ({
      ...prev,
      [action]: true
    }));
  };

  const setReportProcessing = (action: keyof QuickActionReports) => {
    setGeneratedReports(prev => ({
      ...prev,
      [action]: {
        filename: '',
        generatedAt: new Date().toISOString(),
        status: 'processing'
      }
    }));
  };

  const setReportReady = (action: keyof QuickActionReports, filename: string) => {
    setGeneratedReports(prev => ({
      ...prev,
      [action]: {
        filename,
        generatedAt: new Date().toISOString(),
        status: 'ready'
      }
    }));
  };

  const setReportError = (action: keyof QuickActionReports) => {
    setGeneratedReports(prev => ({
      ...prev,
      [action]: {
        filename: '',
        generatedAt: new Date().toISOString(),
        status: 'error'
      }
    }));
  };

  // Fetch API quick actions with history
  const fetchApiQuickActions = async () => {
    try {
      const currentUserId = localStorage.getItem('userId') || '';
      const isProfessional = role?.toLowerCase() === "analyst" || role?.toLowerCase() === "advisor";
      
      // If a client is selected, use their ID as the userId for quick actions
      //const userIdToUse = (isProfessional && isClientSelected) ? selectedClient : currentUserId;
      const userIdToUse = currentUserId;
      const clientId = effectiveClientId;
      
      const response = await aiService.getAIQuickActionsWithLatestHistory(userIdToUse, clientId);
      
      // Handle direct JSON response or wrapped response
      const data = response.data || response;
      
      if (data && Array.isArray(data)) {
        setApiQuickActions(data);
        combineQuickActions(data);
      } else {
        console.error('Failed to fetch quick actions: Invalid response format', response);
      }
    } catch (error) {
      console.error('Error fetching quick actions:', error);
    }
  };

  // Combine API data with metadata, prioritizing API types
  const combineQuickActions = (apiData: any[]) => {
    const combined: CombinedQuickAction[] = [];
    const newPrefsFromApi: Partial<QuickActionPreferences> = {};
    
    // First, add all quick actions that exist in API response
    apiData.forEach(apiItem => {
      const actionType = apiItem.actionType as QuickActionType;
      if (actionType in QUICK_ACTION_METADATA && !combined.some(item => item.type === actionType)) {
        combined.push({
          type: actionType,
          metadata: QUICK_ACTION_METADATA[actionType],
          apiData: apiItem,
          hasApiData: true
        });

        // Sync API enabled status
        newPrefsFromApi[actionType] = apiItem.enabled;
        
        // Sync API status with generatedReports state
        setGeneratedReports(prev => ({
          ...prev,
          [actionType]: {
            filename: apiItem.reportFilename || prev[actionType]?.filename || '',
            generatedAt: apiItem.completedAt || prev[actionType]?.generatedAt || new Date().toISOString(),
            status: apiItem.status === 'completed' ? 'ready' : (apiItem.status as QuickActionReportStatus || 'none')
          }
        }));
      }
    });

    // Update preferences if we got data from API
    if (Object.keys(newPrefsFromApi).length > 0) {
      setQuickActionPrefs(prev => ({
        ...prev,
        ...newPrefsFromApi
      }));
    }
    
    // Then, add any remaining quick actions from metadata that weren't in API response
    Object.keys(QUICK_ACTION_METADATA).forEach(key => {
      const actionType = key as QuickActionType;
      const alreadyAdded = combined.some(item => item.type === actionType);
      
      if (!alreadyAdded) {
        combined.push({
          type: actionType,
          metadata: QUICK_ACTION_METADATA[actionType],
          hasApiData: false
        });
      }
    });
    
    // Sort by API data presence (API items first) then by name
    combined.sort((a, b) => {
      if (a.hasApiData !== b.hasApiData) {
        return b.hasApiData ? 1 : -1; // API items first
      }
      return a.metadata.name.localeCompare(b.metadata.name);
    });
    
    setCombinedQuickActions(combined);
  };

  const toggleInsightDetails = (id: string) => {
    setExpandedInsights((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Fetch AI tasks
  const fetchAITasks = async (priority = null, maxResults = 10) => {
    try {
      const currentUserId = localStorage.getItem('userId');
      if (!currentUserId) return;

      const isProfessional = role?.toLowerCase() === "analyst" || role?.toLowerCase() === "advisor";
      const userIdToUse = (isProfessional && isClientSelected) 
        ? selectedClient 
        : currentUserId;

      const clientId = (isProfessional && isClientSelected) ? selectedClient : '';
      //const response = await aiService.getAITasks({ userId: userIdToUse, clientId, priority, maxResults });
      const response = await aiService.getAITasks({ userId: currentUserId, clientId, priority, maxResults });
      const data = response.data || response;

      if (data && Array.isArray(data)) {
        setTasks(data.map((t: any) => ({
          ...t,
          status: t.status?.toLowerCase() === 'cancelled' ? 'cancelled'
                : t.status?.toLowerCase() === 'completed' ? 'completed'
                : t.status?.toLowerCase() === 'in-progress' || t.status?.toLowerCase() === 'in_progress' ? 'in-progress'
                : 'pending'
        })));
      }
    } catch (error) {
      console.error('Error fetching AI tasks:', error);
    }
  };

  const handleConfirmTask = async () => {
    if (!confirmingTaskId || !confirmAction) return;
    const taskId = confirmingTaskId;
    const comment = taskComment.trim();
    try {
      if (confirmAction === 'complete') {
        await aiService.updateTaskStatus(taskId, 'COMPLETED', comment);
        setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: 'completed' } : t));
        if (comment) toast.success(`Task completed — ${comment}`);
        else toast.success('Task completed');
      } else {
        await aiService.updateTaskStatus(taskId, 'CANCELLED', comment);
        setTasks(prev => prev.filter(t => t.id !== taskId));
        if (comment) toast.success(`Task cancelled — ${comment}`);
        else toast.success('Task cancelled');
      }
    } catch (error) {
      toast.error('Failed to update task');
    } finally {
      setConfirmingTaskId(null);
      setConfirmAction(null);
      setTaskComment('');
    }
  };

  const handleCancelConfirm = () => {
    setConfirmingTaskId(null);
    setConfirmAction(null);
    setTaskComment('');
  };

  // Fetch AI insights
  const fetchAIInsights = async (type = null, maxResults = 10) => {
    try {
      const currentUserId = localStorage.getItem('userId');
      if (!currentUserId) return;
      
      const isProfessional = role?.toLowerCase() === "analyst" || role?.toLowerCase() === "advisor";
      const userIdToUse = (isProfessional && isClientSelected) 
        ? selectedClient 
        : currentUserId;

      const clientId = (isProfessional && isClientSelected) ? selectedClient : '';
      //const response = await aiService.getAIInsights({ userId: userIdToUse, clientId, type, maxResults });
      const response = await aiService.getAIInsights({ userId: currentUserId, clientId, type, maxResults });
      const data = response.data || response;
      
      if (data && Array.isArray(data)) {
        setAiInsights(data);
      }
    } catch (error) {
      console.error('Error fetching AI insights:', error);
    }
  };

  // Fetch API data on component mount and when selected client changes
  useEffect(() => {
    fetchApiQuickActions();
    fetchAITasks();
    fetchAIInsights();
  }, [selectedClient]);

  // Get icon component for quick action
  const getIconComponent = (iconName: string) => {
    switch (iconName) {
      case 'TrendingUp': return <TrendingUp className="h-4 w-4" />;
      case 'Calendar': return <Calendar className="h-4 w-4" />;
      case 'FileText': return <FileText className="h-4 w-4" />;
      case 'BarChart3': return <BarChart3 className="h-4 w-4" />;
      case 'Target': return <Target className="h-4 w-4" />;
      case 'Brain': return <Brain className="h-4 w-4" />;
      default: return <Sparkles className="h-4 w-4" />;
    }
  };

  const handleOpenEditDialog = (action: CombinedQuickAction) => {
    setEditingAction(action.type);
    setEditActionName(action.metadata.name);
    setEditPrompt(action.apiData?.prompt || action.metadata.prompt);
    setIsEditDialogOpen(true);
  };

  const handleSaveQuickAction = async () => {
    if (!editingAction) return;
    
    setIsSavingAction(true);
    try {
      const quickAction = combinedQuickActions.find(qa => qa.type === editingAction);
      if (!quickAction) throw new Error("Action not found");

      const dto: AIQuickActionWithLatestHistoryDTO = {
        // Preference fields
actionId: quickAction.apiData?.actionId || '',
        userId: localStorage.getItem('userId') || '',
        clientId: effectiveClientId || null,
        actionType: action,
        actionName: quickAction.metadata.name,
        description: quickAction.metadata.description,
        parameters: {
          ...(quickAction.apiData?.parameters || {}),
          format: quickActionFormat
        },
        enabled: quickActionPrefs[editingAction],
        createdAt: quickAction.apiData?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        
        // Latest History fields
        historyId: quickAction.apiData?.historyId,
        status: quickAction.apiData?.status,
        triggerType: quickAction.apiData?.triggerType,
        reportFilename: quickAction.apiData?.reportFilename,
        reportUrl: quickAction.apiData?.reportUrl,
        fileSizeBytes: quickAction.apiData?.fileSizeBytes,
        resultData: quickAction.apiData?.resultData,
        historyCreatedAt: quickAction.apiData?.historyCreatedAt,
        completedAt: quickAction.apiData?.completedAt,
        processingTimeMs: quickAction.apiData?.processingTimeMs,
        
        // New fields
        canRun: false, 
        prompt: editPrompt,
        reportType: 'PDF'
      };

      await aiService.executeQuickActionUpdate(dto);
      toast.success("Quick action updated successfully");
      setIsEditDialogOpen(false);
      await fetchApiQuickActions();
    } catch (error) {
      console.error('Error saving quick action:', error);
      toast.error("Failed to update quick action");
    } finally {
      setIsSavingAction(false);
    }
  };

  const toggleQuickAction = async (action: QuickActionType) => {
    const wasDisabled = !quickActionPrefs[action];
    const newEnabledState = !quickActionPrefs[action];

    // Update local state
    setQuickActionPrefs(prev => ({
      ...prev,
      [action]: newEnabledState
    }));

    // Find the quick action data
    const quickAction = combinedQuickActions.find(qa => qa.type === action);
    if (!quickAction) {
      toast.error("Quick action not found");
      return;
    }

    try {
      // Build the DTO for API call
      const dto: AIQuickActionWithLatestHistoryDTO = {
        // Preference fields
        actionId: quickAction.apiData?.actionId || '',
        userId: localStorage.getItem('userId') || '',
        clientId: (role?.toLowerCase() === "analyst" || role?.toLowerCase() === "advisor") && isClientSelected ? selectedClient : '',
        actionType: action,
        actionName: quickAction.metadata.name,
        description: quickAction.metadata.description,
        parameters: {
          ...(quickAction.apiData?.parameters || {}),
          format: quickActionFormat
        },
        enabled: newEnabledState,
        createdAt: quickAction.apiData?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        
        // Latest History fields
        historyId: quickAction.apiData?.historyId,
        status: quickAction.apiData?.status,
        triggerType: quickAction.apiData?.triggerType,
        reportFilename: quickAction.apiData?.reportFilename,
        reportUrl: quickAction.apiData?.reportUrl,
        fileSizeBytes: quickAction.apiData?.fileSizeBytes,
        resultData: quickAction.apiData?.resultData,
        historyCreatedAt: quickAction.apiData?.historyCreatedAt,
        completedAt: quickAction.apiData?.completedAt,
        processingTimeMs: quickAction.apiData?.processingTimeMs,
        
        // New fields
        canRun: false, // Default to false, only set true during actual rerun
        prompt: quickAction.apiData?.prompt || quickAction.metadata.prompt, // Keep persisted prompt if any
        reportType: 'PDF' // Set report type to PDF for toggle
      };

      // Call the API to update the quick action
      const response = await aiService.executeQuickActionUpdate(dto);
      
      if (response.data || response) {
        if (wasDisabled) {
          // Action is being enabled
          toast.success("Quick action enabled");

          // Auto-run the action if it hasn't been run before
          if (!actionHistory[action]) {
            // Small delay to allow state update
            setTimeout(async () => {
              await handleRerunAction(action);
            }, 500);
          } else {
            toast.info("Quick action enabled. Click to rerun.");
          }
        } else {
          // Action is being disabled
          toast.success("Quick action disabled");
        }
        
        // Refresh quick actions to get updated status
        await fetchApiQuickActions();
      } else {
        throw new Error("No response data received");
      }
    } catch (error) {
      console.error('Error updating quick action:', error);
      toast.error("Failed to update quick action");
      
      // Revert state change on error
      setQuickActionPrefs(prev => ({
        ...prev,
        [action]: !newEnabledState
      }));
    }
  };

  const runQuickAction = async (action: keyof QuickActionPreferences) => {
    // Set processing status
    setReportProcessing(action);
    toast.info("Generating report in background...");

    // Simulate background processing with a small delay
    setTimeout(() => {
      switch (action) {
      case 'portfolioReport':
        handleGeneratePortfolioReport(getClientContext, generateAndStorePDFReport as any, quickActionFormat);
        break;
      /* case 'scheduleMeeting':
        handleScheduleMeeting(getClientContext, generateAndStorePDFReport, quickActionFormat);
        break;
       */
      case 'investmentProposal':
        handleCreateInvestmentProposal(getClientContext, generateAndStorePDFReport as any, quickActionFormat);
        break;
      case 'riskAnalysis':
        handleRunRiskAnalysis(getClientContext, generateAndStorePDFReport as any, quickActionFormat);
        break;
      case 'financialGoals':
        handleReviewFinancialGoals(getClientContext, generateAndStorePDFReport as any, quickActionFormat);
        break;
      case 'marketInsights':
        handleGetMarketInsights(getClientContext, generateAndStorePDFReport as any, quickActionFormat);
        break;
      }
    }, 1500);
  };

  const generateAndStorePDFReport = (action: keyof QuickActionReports, content: string, filename: string, title: string) => {
    try {
      const blob = downloadPDFReport(content, filename, title);

      if (!blob) {
        setReportError(action);
        toast.error('Failed to generate PDF report');
        return;
      }

      // Create a URL for the blob and store it
      const url = URL.createObjectURL(blob);

      // Store the report metadata (but don't download yet)
      setReportReady(action, filename);

      // Store blob URL temporarily in sessionStorage for later download
      sessionStorage.setItem(`report_${action}`, url);

      toast.success('Report ready! Click the download link to save.');
    } catch (error) {
      console.error('PDF generation error:', error);
      setReportError(action);
      toast.error('Failed to generate PDF report');
    }
  };

  const downloadReport = (action: keyof QuickActionReports) => {
    const report = generatedReports[action];
    if (!report || report.status !== 'ready') {
      toast.error('Report not available');
      return;
    }

    const url = sessionStorage.getItem(`report_${action}`);
    if (!url) {
      toast.error('Report link expired. Please rerun the action.');
      return;
    }

    const link = document.createElement('a');
    link.href = url;
    link.download = report.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success(`Downloaded: ${report.filename}`);
  };

  const handleRerunAction = async (action: QuickActionType) => {
    if (runningActionsRef.current.has(action)) {
      toast.info("Already regenerating, please wait...");
      return;
    }

    if ((role?.toLowerCase() === "analyst" || role?.toLowerCase() === "advisor") && !isClientSelected) {
      toast.error("Please select a client first");
      return;
    }

    // Find the quick action data
    const quickAction = combinedQuickActions.find(qa => qa.type === action);
    if (!quickAction) {
      toast.error("Quick action not found");
      return;
    }

    // Set processing status
    runningActionsRef.current.add(action);
    setReportProcessing(action);

    try {
      toast.info("Regenerating report...");

      // Build the DTO for API call
      const dto: AIQuickActionWithLatestHistoryDTO = {
        // Preference fields
        actionId: quickAction.apiData?.actionId || '',
        userId: localStorage.getItem('userId') || '',
        clientId: (role?.toLowerCase() === "analyst" || role?.toLowerCase() === "advisor") && isClientSelected ? selectedClient : '',
        actionType: action,
        actionName: quickAction.metadata.name,
        description: quickAction.metadata.description,
        parameters: {
          ...(quickAction.apiData?.parameters || {}),
          format: quickActionFormat
        },
        enabled: quickActionPrefs[action],
        createdAt: quickAction.apiData?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        
        // Latest History fields
        historyId: quickAction.apiData?.historyId,
        status: 'processing',
        triggerType: 'manual',
        reportFilename: undefined,
        reportUrl: undefined,
        fileSizeBytes: undefined,
        resultData: undefined,
        historyCreatedAt: undefined,
        completedAt: undefined,
        processingTimeMs: undefined,
        
        // New fields
        canRun: true,
        prompt: quickAction.apiData?.prompt || quickAction.metadata.prompt,
        reportType: 'PDF' // Set report type to PDF for rerun
      };

      // Call the new API endpoint
      const response = await aiService.executeQuickActionUpdate(dto);
      
      if (response.data || response) {
        toast.success("Report regeneration started successfully");
        markActionAsRun(action);
        
        // Refresh quick actions to get updated status
        await fetchApiQuickActions();
      } else {
        throw new Error("No response data received");
      }
    } catch (error) {
      console.error('Error rerunning quick action:', error);
      toast.error("Failed to regenerate report");
      setReportError(action);
    } finally {
      runningActionsRef.current.delete(action);
    }
  };

  const downloadPDFReport = (content: string, filename: string, title: string) => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const maxLineWidth = pageWidth - (margin * 2);
      let yPosition = margin;

      // Add title
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text(title, margin, yPosition);
      yPosition += 10;

      // Add generation date
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated: ${new Date().toLocaleString()}`, margin, yPosition);
      yPosition += 15;

      // Add separator line
      doc.setLineWidth(0.5);
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 10;

      // Process content
      doc.setFontSize(10);
      const lines = content.split('\n');

      for (const line of lines) {
        // Check if we need a new page
        if (yPosition > pageHeight - margin) {
          doc.addPage();
          yPosition = margin;
        }

        if (line.trim() === '') {
          yPosition += 5;
          continue;
        }

        // Check if line is a separator
        if (line.includes('━')) {
          doc.setLineWidth(0.3);
          doc.line(margin, yPosition, pageWidth - margin, yPosition);
          yPosition += 8;
          continue;
        }

        // Check if line is a header (all caps or starts with specific patterns)
        const isHeader = line.trim().match(/^[A-Z\s]+$/) && line.trim().length < 50;
        const isBullet = line.trim().startsWith('•') || line.trim().startsWith('✓') || line.trim().startsWith('○');

        if (isHeader && line.trim().length > 0) {
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(12);
        } else if (isBullet) {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(10);
        } else {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(10);
        }

        // Split long lines
        const wrappedLines = doc.splitTextToSize(line, maxLineWidth);
        for (const wrappedLine of wrappedLines) {
          if (yPosition > pageHeight - margin) {
            doc.addPage();
            yPosition = margin;
          }
          doc.text(wrappedLine, margin, yPosition);
          yPosition += 6;
        }
      }

      // Add footer with branding
      const totalPages = doc.internal.pages.length - 1;
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'italic');
        doc.text(
          'Generated by WealthBrainAI AI Assistant',
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        );
        doc.text(
          `Page ${i} of ${totalPages}`,
          pageWidth - margin,
          pageHeight - 10,
          { align: 'right' }
        );
      }

      // Return PDF as blob (don't auto-download)
      return doc.output('blob');
    } catch (error) {
      console.error('PDF generation error:', error);
      return null;
    }
  };

  const getClientContext = () => {
    const clientName = isClientSelected && (role?.toLowerCase() === "analyst" || role?.toLowerCase() === "advisor")
      ? clients.find(c => c.id === selectedClient)?.name
      : null;
    const portfolioValue = isClientSelected && (role?.toLowerCase() === "analyst" || role?.toLowerCase() === "advisor")
      ? clients.find(c => c.id === selectedClient)?.portfolio
      : "$850K";

    return {
      name: clientName || "Your",
      portfolioValue,
      possessive: clientName ? `${clientName}'s` : "Your"
    };
  };

  const [uploadedDocuments, setUploadedDocuments] = useState<UploadedDocument[]>([
    {
      id: "1",
      name: "Q4_2024_Financial_Statement.pdf",
      type: "PDF",
      size: "2.4 MB",
      uploadedBy: "Emma Watson",
      uploadDate: "Oct 20, 2025",
      status: "ready",
    },
    {
      id: "2",
      name: "Investment_Prospectus_Healthcare.pdf",
      type: "PDF",
      size: "1.8 MB",
      uploadedBy: "Sarah Chen",
      uploadDate: "Oct 19, 2025",
      status: "ready",
    },
    {
      id: "3",
      name: "Market_Research_Tech_Sector.docx",
      type: "DOCX",
      size: "956 KB",
      uploadedBy: "Michael Torres",
      uploadDate: "Oct 18, 2025",
      status: "processing",
    },
  ]);

  const [documentResearch, setDocumentResearch] = useState<DocumentResearch[]>([
    {
      id: "1",
      documentId: "1",
      documentName: "Q4_2024_Financial_Statement.pdf",
      type: "summary",
      title: "Executive Summary",
      content: "The Q4 2024 financial statement shows strong performance with revenue growth of 12.3% YoY. Net profit margins improved from 8.2% to 9.7%. Key drivers include successful cost optimization initiatives and increased market share in core segments. The company maintains a healthy balance sheet with a debt-to-equity ratio of 0.42.",
      confidence: 95,
      createdAt: "2 hours ago",
    },
    {
      id: "2",
      documentId: "1",
      documentName: "Q4_2024_Financial_Statement.pdf",
      type: "insights",
      title: "Key Financial Insights",
      content: "• Operating cash flow increased 18% to $45M\n• Working capital management improved significantly\n• R&D spending up 22%, indicating strong innovation pipeline\n• Dividend yield of 2.8% with 15-year consecutive growth\n• Price-to-earnings ratio of 18.5x vs industry average of 21.2x suggests undervaluation",
      confidence: 92,
      createdAt: "2 hours ago",
    },
    {
      id: "3",
      documentId: "2",
      documentName: "Investment_Prospectus_Healthcare.pdf",
      type: "recommendations",
      title: "Investment Recommendations",
      content: "Based on the prospectus analysis:\n\n1. STRONG BUY for long-term investors (5+ years)\n2. Fund focuses on innovative biotech and medical devices\n3. Historical returns: 24% annually over past 5 years\n4. Low correlation (0.23) with S&P 500 provides diversification\n5. ESG score: 87/100 - excellent sustainability practices\n\nRisk Level: Moderate-High\nRecommended Allocation: 5-10% of growth portfolio",
      confidence: 88,
      createdAt: "1 day ago",
    },
    {
      id: "4",
      documentId: "2",
      documentName: "Investment_Prospectus_Healthcare.pdf",
      type: "risk-analysis",
      title: "Risk Assessment",
      content: "Identified Risks:\n• Regulatory risk: FDA approval timelines for biotech holdings\n• Sector concentration: 78% in biotech vs diversified healthcare\n• Volatility: Beta of 1.45 indicates higher market sensitivity\n• Liquidity: Some holdings in pre-revenue stage companies\n\nMitigation Strategies:\n• Fund manager has 20+ years experience navigating FDA process\n• Diversification across 40+ holdings reduces single-stock risk\n• Strong cash reserves (15% of portfolio) for opportunistic investing",
      confidence: 90,
      createdAt: "1 day ago",
    },
  ]);

  const [messages, setMessages] = useState<Message[]>([
    /* {
      id: "1",
      sender: "AI Assistant",
      role: "ai",
      content: "Hello! I've analyzed the current portfolio and identified 3 opportunities for discussion. Would you like me to share them with the team?",
      timestamp: "10:30 AM",
      aiGenerated: true,
    },
    {
      id: "2",
      sender: "Sarah Chen",
      role: "analyst",
      content: "Yes, please share. I've been reviewing the tech sector allocation and have some insights to add.",
      timestamp: "10:32 AM",
    },
    {
      id: "3",
      sender: "AI Assistant",
      role: "ai",
      content: "Based on recent market trends, I recommend:\n\n1. Rebalancing tech sector (currently 35%, suggest 30%)\n2. Increasing healthcare exposure (ESG-focused funds showing +18% YTD)\n3. Consider tax-loss harvesting in Q4 for optimal returns\n\nI've generated detailed reports for each recommendation.",
      timestamp: "10:33 AM",
      aiGenerated: true,
    },
    {
      id: "4",
      sender: "Michael Torres",
      role: "advisor",
      content: "The tax-loss harvesting recommendation aligns well with Emma's financial goals. Let's schedule a meeting to discuss implementation.",
      timestamp: "10:35 AM",
    }, */
  ]);

  const [inputMessage, setInputMessage] = useState("");
  const [selectedContext, setSelectedContext] = useState("portfolio");
  const [chatFormat, setChatFormat] = useState<AIStructuredFormat>("text");
  const [selectedAgent, setSelectedAgent] = useState("general");
  const isAgentMode = selectedAgent !== "general";
  const [reportingMessageId, setReportingMessageId] = useState<string | null>(null);
  const [scheduleAction, setScheduleAction] = useState<CombinedQuickAction | null>(null);
  const [intakeOpen, setIntakeOpen] = useState(false);
  const chatInputRef = useRef<HTMLInputElement>(null);
  const [reportHistory, setReportHistory] = useState<any[]>([]);
  const [reportHistoryLoading, setReportHistoryLoading] = useState(false);
  const [reportedMessageIds, setReportedMessageIds] = useState<Set<string>>(new Set());
  const [deletingReportId, setDeletingReportId] = useState<string | null>(null);
  const [expandedReportDomains, setExpandedReportDomains] = useState<Record<string, boolean>>({});

  const applyMissingPrompt = (prompt: string) => {
    setInputMessage(prompt);
    setTimeout(() => chatInputRef.current?.focus(), 50);
  };

  const fetchReportHistory = async () => {
    const clientId = effectiveClientId || (localStorage.getItem('userId') || '');
    if (!clientId) {
      setReportHistory([]);
      return;
    }
    setReportHistoryLoading(true);
    try {
      const resp: any = await aiService.getAgentReports(clientId);
      setReportHistory(resp.data || resp || []);
    } catch (e) {
      console.error('Error loading report history:', e);
      setReportHistory([]);
    } finally {
      setReportHistoryLoading(false);
    }
  };

  const openReport = async (url: string) => {
    try {
      const signed = await aiService.generateS3PresignedUrl(url);
      const downloadUrl = typeof signed === 'string'
        ? signed
        : (signed?.data?.url || signed?.url || signed?.presignedUrl || url);
      window.open(downloadUrl, '_blank');
    } catch (e) {
      console.error('Error opening report:', e);
      window.open(url, '_blank');
    }
  };

  const handleDeleteReport = async (r: any) => {
    if (!window.confirm('Delete this report? The history record and the PDF file will be permanently removed.')) return;
    setDeletingReportId(r.id);
    try {
      await aiService.deleteAgentReport(r.id);
      toast.success('Report deleted');
      setReportHistory((prev) => prev.filter((item) => item.id !== r.id));
    } catch (e: any) {
      console.error('Error deleting report:', e);
      toast.error(e?.message || 'Failed to delete report');
    } finally {
      setDeletingReportId(null);
    }
  };

  useEffect(() => {
    fetchReportHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient, isClientSelected, role]);
  const [quickActionFormat, setQuickActionFormat] = useState<AIStructuredFormat>("detailed_report");

  // Fetch signed clients
  const fetchSignedClients = async () => {
    setClientsLoading(true);
    try {
      const userId = localStorage.getItem('userId');
      if (!userId) return;

      const response = await engagementService.getMyAgreementsWithPortfolioValue(userId);
      const agreements = response.data || response;

      if (Array.isArray(agreements)) {
        // Filter for signed agreements and fetch profile details for each client
        const signedAgreements = agreements.filter((a: any) => a.status === 'SIGNED_BY_BOTH');
        
        const clientPromises = signedAgreements.map(async (a: any) => {
          const isRequester = a.requesterId === userId;
          const clientId = isRequester ? a.recipientId : a.requesterId;
          
          try {
            // Fetch the actual user profile to get the email
            const profile = await userService.getProfile(clientId);
            const userData = profile.data || profile;
            
            return {
              id: clientId,
              name: userData.fullName || userData.name || a.clientName || `Client ${clientId.substring(0, 4)}`,
              portfolio: a.totalClientWorth ? `$${a.totalClientWorth.toLocaleString()}` : "N/A",
              email: userData.email || "",
              role: "investor"
            };
          } catch (error) {
            console.error(`Error fetching profile for client ${clientId}:`, error);
            return {
              id: clientId,
              name: a.clientName || `Client ${clientId.substring(0, 4)}`,
              portfolio: a.totalClientWorth ? `$${a.totalClientWorth.toLocaleString()}` : "N/A",
              email: "",
              role: "investor"
            };
          }
        });

        const signedClients = await Promise.all(clientPromises);

        setClientList([
          // { id: "all", name: "All Clients", portfolio: "$12.5M", email: "", role: "system" },
          ...signedClients
        ]);
      }
    } catch (error) {
      console.error('Error fetching signed clients:', error);
    } finally {
      setClientsLoading(false);
    }
  };

  useEffect(() => {
    if (role && (role.toLowerCase() === 'advisor' || role.toLowerCase() === 'analyst')) {
      fetchSignedClients();
    }
  }, [role]);

  const [aiInsights, setAiInsights] = useState<AIInsight[]>([]);
  const [expandedInsights, setExpandedInsights] = useState<Record<string, boolean>>({});
  const [tasks, setTasks] = useState<Task[]>([]);
  const runningActionsRef = useRef<Set<string>>(new Set());
  const [confirmingTaskId, setConfirmingTaskId] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<'complete' | 'cancel' | null>(null);
  const [taskComment, setTaskComment] = useState('');
  
  /* const aiInsights: AIInsight[] = [
    {
      id: "1",
      type: "recommendation",
      title: "Portfolio Rebalancing Opportunity",
      description: "Tech sector overweight by 5%. Recommend reducing to target allocation for better risk-adjusted returns.",
      confidence: 92,
      relevantTo: ["investor", "advisor"],
      timestamp: "2 hours ago",
    },
    {
      id: "2",
      type: "alert",
      title: "Market Volatility Alert",
      description: "Increased volatility detected in energy sector. Consider hedging strategies for exposed positions.",
      confidence: 87,
      relevantTo: ["analyst", "advisor"],
      timestamp: "4 hours ago",
    },
    {
      id: "3",
      type: "opportunity",
      title: "ESG Investment Opportunity",
      description: "Healthcare ESG funds showing strong performance (+18% YTD) with low correlation to existing holdings.",
      confidence: 85,
      relevantTo: ["investor", "analyst", "advisor"],
      timestamp: "6 hours ago",
    },
    {
      id: "4",
      type: "analysis",
      title: "Risk-Adjusted Return Analysis",
      description: "Current Sharpe ratio (1.85) exceeds benchmark. Portfolio optimization suggests minor adjustments for 2026.",
      confidence: 94,
      relevantTo: ["investor", "advisor"],
      timestamp: "1 day ago",
    },
  ]; */

  /* const tasks: Task[] = [
    {
      id: "1",
      title: "Review rebalancing proposal for Q4",
      assignedTo: "Financial Advisor",
      priority: "high",
      status: "in-progress",
      aiSuggested: true,
    },
    {
      id: "2",
      title: "Analyze ESG healthcare fund options",
      assignedTo: "Financial Analyst",
      priority: "high",
      status: "pending",
      aiSuggested: true,
    },
    {
      id: "3",
      title: "Schedule portfolio review meeting",
      assignedTo: "Financial Advisor",
      priority: "medium",
      status: "pending",
      aiSuggested: true,
    },
    {
      id: "4",
      title: "Prepare tax-loss harvesting strategy",
      assignedTo: "Financial Advisor",
      priority: "medium",
      status: "pending",
      aiSuggested: true,
    },
  ]; */

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessageContent = inputMessage;
    const newMessage: Message = {
      id: Date.now().toString(),
      sender: "You",
      role: "investor",
      content: userMessageContent,
      createdAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };

    setMessages(prev => [...prev, newMessage]);
    setInputMessage("");
    setIsLoading(true);

    try {
      // Save user message to backend if session is active
      if (currentSession && currentSession.id) {
        try {
          await aiService.saveChatMessage({
            sessionId: currentSession.id,
            role: "user",
            content: userMessageContent,
            prompt: userMessageContent,
            metadata: {
              context: selectedContext,
              format: chatFormat
            },
            createdAt: new Date().toISOString()
          });
        } catch (error) {
          console.error('Error saving user message:', error);
        }
      }

      // Get AI response from selected service
      let aiResponseText: string;
      let aiStructuredJson: any;
      let aiDomain: string | undefined;

      if (isAgentMode) {
        const agentPayload: any = {
          message: userMessageContent,
          context: selectedAgent,
          clientId: effectiveClientId || null,
        };
        // Build portfolio/onboarding context the same way as the general chat
        try {
          const portfolioResp: any = sharePortfolio
            ? await (isClientSelected
                ? portfolioService.getAllPortfoliosByUserId(selectedClient)
                : portfolioService.getAllPortfoliosByUserId(localStorage.getItem("userId") || ""))
            : null;
          agentPayload.portfolioData = portfolioResp?.data || portfolioResp ? JSON.stringify(portfolioResp?.data || portfolioResp) : null;

          const onboardingResp: any = shareGoals
            ? await (isClientSelected
                ? investorService.getInvestorOnboardingDetailsByUserId(selectedClient)
                : investorService.getInvestorOnboardingDetailsByUserId(localStorage.getItem("userId") || ""))
            : null;
          agentPayload.onboardingData = onboardingResp?.data || onboardingResp ? JSON.stringify(onboardingResp?.data || onboardingResp) : null;
        } catch (error) {
          console.error("Error building agent context:", error);
        }

        const agentResponse = await aiService.invokeAgent(selectedAgent, agentPayload);
        aiResponseText = agentResponse.content || (agentResponse.error ? `Error: ${agentResponse.error}` : "No response");
        aiStructuredJson = agentResponse.structuredJson;
        aiDomain = agentResponse.domain || selectedAgent;
      } else {
        aiResponseText = await generateAIResponse(userMessageContent, selectedContext, chatFormat);
        aiDomain = "general";
      }

      const aiResponse: Message = {
        id: (Date.now() + 1).toString(),
        sender: "AI Assistant",
        role: "ai",
        content: aiResponseText,
        createdAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        aiGenerated: true,
        structuredJson: aiStructuredJson,
        domain: aiDomain,
      };
      
      setMessages(prev => [...prev, aiResponse]);

      // Save AI response to backend if session is active
      if (currentSession && currentSession.id) {
        try {
          await aiService.saveChatMessage({
            sessionId: currentSession.id,
            role: "assistant",
            content: aiResponseText,
            metadata: {
              aiGenerated: true,
              context: selectedContext,
              format: chatFormat
            },
            createdAt: new Date().toISOString()
          });
        } catch (error) {
          console.error('Error saving AI response:', error);
        }
      }
    } catch (error) {
      console.error('Send message error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateAgentReport = async (message: Message) => {
    if (!message.domain) return;
    setReportingMessageId(message.id);
    try {
      const resp = await aiService.agentCreateReport(message.domain, {
        domain: message.domain,
        content: message.content,
        structuredJson: message.structuredJson,
        clientId: effectiveClientId || (localStorage.getItem('userId') || undefined),
      });
      const reportUrl = resp?.reportUrl;
      if (!reportUrl) throw new Error("No report URL returned");
      const signed = await aiService.generateS3PresignedUrl(reportUrl);
      // The endpoint returns the presigned URL as a plain string (or JSON { url }).
      const downloadUrl = typeof signed === 'string'
        ? signed
        : (signed?.data?.url || signed?.url || signed?.presignedUrl || reportUrl);
      window.open(downloadUrl, "_blank");
      toast.success("Report generated");
      setReportedMessageIds((prev) => new Set(prev).add(message.id));
      fetchReportHistory();
    } catch (e: any) {
      console.error("Create report error:", e);
      toast.error(e?.message || "Failed to generate report");
    } finally {
      setReportingMessageId(null);
    }
  };

  const handleToggleChatSession = async () => {
    if (isChatSessionActive) {
      setIsChatSessionActive(false);
      setMessages([]);
      setCurrentSession(null);
      toast.info("Chat session ended");
    } else {
      try {
        setIsLoading(true);
        const userId = localStorage.getItem('userId');
        const clientId = effectiveClientId || null;
        const clientName = clientId ? clients.find(c => c.id === clientId)?.name : null;
        
        const sessionTitle = clientName 
          ? `Chat regarding ${clientName}'s Portfolio` 
          : "New AI Chat Session";

        const sessionDto = {
          userId,
          clientId,
          sessionTitle,
          contextData: {
            role,
            selectedContext,
            sharePortfolio,
            shareGoals
          },
          isActive: true,
          messages: []
        };

        const response = await aiService.createChatSession(sessionDto);
        const sessionData = response.data || response;
        
        if (sessionData && sessionData.id) {
          setCurrentSession(sessionData);
          setIsChatSessionActive(true);
          setMessages([]);
          toast.success("New chat session started");
        } else {
          throw new Error("Failed to create chat session");
        }
      } catch (error) {
        console.error("Error starting chat session:", error);
        toast.error("Failed to start a new chat session. Please try again.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const [isLoading, setIsLoading] = useState(false);

const generateAIResponse = async (query: string, context: string, format: string): Promise<string> => {
  try {
    setIsLoading(true);
    
    // Determine the email to use (fallback)
    let emailToUse = "";
    const storedUserData = localStorage.getItem("userData");
    const userData = storedUserData ? JSON.parse(storedUserData) : null;
    const userEmail = localStorage.getItem("userEmail");

    const selectedClientData = clients.find(c => c.id === selectedClient);
    if (selectedClientData && selectedClientData.role?.toLowerCase() === "investor") {
      emailToUse = selectedClientData.email;
    } else if (role?.toLowerCase() === "investor") {
      emailToUse = userEmail || userData?.email;
    }

    let portfolioDataWithHoldings = null;
    if (sharePortfolio) {
      try {
        if (isClientSelected) {
          const portfolioResponse: any = await portfolioService.getAllPortfoliosByUserId(selectedClient);
          portfolioDataWithHoldings = portfolioResponse.data || portfolioResponse;
        } else if (emailToUse) {
          const portfolioResponse: any = await portfolioService.getAllPortfoliosByEmail(emailToUse);
          portfolioDataWithHoldings = portfolioResponse.data || portfolioResponse;
        }
      } catch (error) {
        console.error('Error fetching portfolio data:', error);
      }
    }

    let onboardingDetails = null;
    if (shareGoals) {
      try {
        if (isClientSelected) {
          // If it's a specific client, fetch by userId
          const onboardingResponse = await investorService.getInvestorOnboardingDetailsByUserId(selectedClient);
          onboardingDetails = onboardingResponse.data || onboardingResponse;
        } else if (emailToUse) {
          // For current user or legacy fallback
          const onboardingResponse = await investorService.getInvestorOnboardingDetailsByEmail(emailToUse);
          onboardingDetails = onboardingResponse.data || onboardingResponse;
        }
      } catch (error) {
        console.error('Error fetching onboarding details:', error);
      }
    }
    
    // Enrich query with format if not plain text
    const formattedQuery = format !== 'text' 
      ? `${query}\n\nPlease provide the response in ${format.replace(/_/g, ' ')} format.`
      : query;
    
    // Call the selected AI service
    const response = await aiServiceSelector.sendMessage(
      formattedQuery, 
      context, 
      JSON.stringify({
        portfolio: portfolioDataWithHoldings,
        onboarding: onboardingDetails
      })
    );
    const newResponse = typeof response === 'string' ? response : String(response || 'No response');
    return newResponse;
  } catch (error) {
    console.error('AI response error:', error);
    return "I apologize, but I'm having trouble connecting to my analysis services. Please try again in a moment.";
  } finally {
    setIsLoading(false);
  }
};

  return (
    <div className="space-y-6">
      {/* AI Status Banner */}
      <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950 dark:border-blue-800">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900">
              <Sparkles className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1">
              <h3 className="mb-1">AI Assistant Active</h3>
              <p className="text-sm text-muted-foreground">
                {role?.toLowerCase() === "investor" 
                  ? "I'm monitoring your portfolio, analyzing market trends, and facilitating collaboration between your team members. I've identified 3 new insights since your last session."
                  : !isClientSelected
                  ? "I'm monitoring all client portfolios, analyzing market trends, and providing comprehensive insights across your entire client base. Ready to assist with portfolio research and document analysis."
                  : `I'm analyzing ${clients.find(c => c.id === selectedClient)?.name}'s portfolio and providing personalized insights. AI research tools are ready for this client's portfolio and documents.`
                }
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Client Selector for Analysts and Advisors */}
      {(role?.toLowerCase() === "analyst" || role?.toLowerCase() === "advisor") && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">Client Selection</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Select a client to perform portfolio research and AI analysis
                </p>
              </div>
              <div className="flex items-center gap-2">
                {clientsLoading && (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-label="Loading clients" />
                )}
                <Select value={selectedClient} onValueChange={setSelectedClient}>
                  <SelectTrigger className="w-[280px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="self">
                      <div className="flex items-center gap-2">
                        <UserCircle className="h-4 w-4" />
                        <span>Self (No client)</span>
                      </div>
                    </SelectItem>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        <div className="flex items-center justify-between gap-3">
                          <span>{client.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {client.portfolio}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          {isClientSelected && (
            <CardContent>
              <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-3 flex-1">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {clients.find(c => c.id === selectedClient)?.name.split(' ').map(n => n[0]).join('')}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">
                      {clients.find(c => c.id === selectedClient)?.name}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Portfolio Value: {clients.find(c => c.id === selectedClient)?.portfolio}
                    </p>
                  </div>
                </div>
                <Badge variant="secondary" className="gap-1">
                  <Sparkles className="h-3 w-3" />
                  AI Ready
                </Badge>
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Main Tabs: Portfolio Research and Document Research */}
      <Tabs defaultValue="portfolio-research" className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-md mx-auto">
          <TabsTrigger value="portfolio-research">
            <BarChart3 className="h-4 w-4 mr-2" />
            Portfolio Research
          </TabsTrigger>
          <TabsTrigger value="document-research" disabled>
            <FileText className="h-4 w-4 mr-2" />
            Document Research
          </TabsTrigger>
        </TabsList>

        {/* Portfolio Research Tab */}
        <TabsContent value="portfolio-research" className="space-y-4">
          <Tabs 
            defaultValue="chat" 
            className="w-full"
            onValueChange={(value) => {
              if (value === 'insights') {
                fetchAIInsights();
              } else if (value === 'tasks') {
                fetchAITasks();
              }
            }}
          >
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="chat">
                <MessageSquare className="h-4 w-4 mr-2" />
                AI Chat
              </TabsTrigger>
              <TabsTrigger value="insights">
                <Sparkles className="h-4 w-4 mr-2" />
                Insights
              </TabsTrigger>
              <TabsTrigger value="tasks">
                <CheckCircle className="h-4 w-4 mr-2" />
                Tasks
              </TabsTrigger>
              <TabsTrigger value="analytics" disabled>
                <BarChart3 className="h-4 w-4 mr-2" />
                Analytics
              </TabsTrigger>
            </TabsList>

        {/* AI Chat Tab */}
        <TabsContent value="chat" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            <Card className="lg:col-span-3 min-w-0">
              <CardHeader>
                <div className="flex flex-col gap-3">
                  <div className="flex flex-wrap items-center gap-2 min-w-0">
                    <CardTitle className="whitespace-nowrap">AI Assistant Chat</CardTitle>
                    {(role?.toLowerCase() === "analyst" || role?.toLowerCase() === "advisor") && isClientSelected && (
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <UserCircle className="h-3 w-3" />
                        (Client: {clients.find(c => c.id === selectedClient)?.name})
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-end gap-2 min-w-0">
                    <Button 
                      variant={isChatSessionActive ? "outline" : "default"}
                      size="sm"
                      onClick={handleToggleChatSession}
                      className="h-8 text-xs"
                      disabled={isLoading}
                    >
                      <Sparkles className="h-3 w-3 mr-2" />
                      {isLoading ? "Starting..." : isChatSessionActive ? "End Chat Session" : "Start a new Chat Session"}
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 text-xs"
                      onClick={() => setIntakeOpen(true)}
                      disabled={
                        ((role?.toLowerCase() === "analyst" || role?.toLowerCase() === "advisor") && !isClientSelected)
                      }
                    >
                      <FileText className="h-3 w-3 mr-2" />
                      Financial Intake
                    </Button>

                    {((role?.toLowerCase() === "investor") || 
                      ((role?.toLowerCase() === "analyst" || role?.toLowerCase() === "advisor") && isClientSelected && selectedClient !== "")) && (
                      <div className="flex items-center gap-4 px-2 border-l border-r mx-1">
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="share-portfolio" 
                            checked={sharePortfolio} 
                            onCheckedChange={(checked) => setSharePortfolio(!!checked)}
                            disabled={isChatSessionActive}
                          />
                          <Label 
                            htmlFor="share-portfolio" 
                            className="text-[10px] font-medium leading-none cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                          >
                            Share Portfolio
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="share-goals" 
                            checked={shareGoals} 
                            onCheckedChange={(checked) => setShareGoals(!!checked)}
                            disabled={isChatSessionActive}
                          />
                          <Label 
                            htmlFor="share-goals" 
                            className="text-[10px] font-medium leading-none cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                          >
                            Share Goals
                          </Label>
                        </div>
                      </div>
                    )}
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] text-muted-foreground uppercase font-semibold px-1">Agent</span>
                      <Select value={selectedAgent} onValueChange={setSelectedAgent}>
                        <SelectTrigger className="w-[160px] h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {AGENT_MODES.map((mode) => (
                            <SelectItem key={mode.id} value={mode.id}>
                              {mode.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {!isAgentMode && (
                      <div className="flex flex-col gap-1">
                        <span className="text-[10px] text-muted-foreground uppercase font-semibold px-1">Default Format</span>
                        <Select value={chatFormat} onValueChange={(value: AIStructuredFormat) => setChatFormat(value)}>
                          <SelectTrigger className="w-[140px] h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {AI_STRUCTURED_FORMATS.map((format) => (
                              <SelectItem key={format.value} value={format.value}>
                                {format.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {!isAgentMode && (role?.toLowerCase() === "analyst" || role?.toLowerCase() === "advisor" || role?.toLowerCase() === "investor" ? (
                      <div className="flex flex-col gap-1">
                        <span className="text-[10px] text-muted-foreground uppercase font-semibold px-1">Context</span>
                        <Select value={selectedContext} onValueChange={setSelectedContext}>
                          <SelectTrigger className="w-[120px] h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="portfolio">Portfolio</SelectItem>
                            <SelectItem value="market">Market Analysis</SelectItem>
                            <SelectItem value="tax">Tax Strategy</SelectItem>
                            <SelectItem value="general">General</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    ) : null)}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {!isChatSessionActive ? (
                  <div className="flex flex-col items-center justify-center h-[400px] text-center px-4">
                    <div className="p-4 rounded-full bg-muted mb-4">
                      <MessageSquare className="h-12 w-12 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">No Active Chat Session</h3>
                    <p className="text-sm text-muted-foreground max-w-md mb-4">
                      To enable the AI Assistant chat, please click the "Start a new Chat Session" button above.
                      Starting a new session will allow you to collaborate with AI on portfolio research and analysis.
                    </p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Sparkles className="h-4 w-4" />
                      <span>Start a session to begin intelligent collaboration</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <ScrollArea className="h-[400px] pr-4 mb-4">
                      <div className="space-y-4">
                        {messages.map((message) => (
                          <div
                            key={message.id}
                            className={`flex gap-3 ${
                              message.role === "ai" ? "flex-row" : "flex-row"
                            }`}
                          >
                            <Avatar className="h-8 w-8">
                              <AvatarFallback
                                className={
                                  message.role === "ai"
                                    ? "bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300"
                                    : message.role?.toLowerCase() === "investor"
                                    ? "bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-300"
                                    : message.role?.toLowerCase() === "analyst"
                                    ? "bg-purple-100 text-purple-600 dark:bg-purple-900 dark:text-purple-300"
                                    : "bg-orange-100 text-orange-600 dark:bg-orange-900 dark:text-orange-300"
                                }
                              >
                                {message.role === "ai" ? (
                                  <Sparkles className="h-4 w-4" />
                                ) : (
                                  message.sender.split(" ").map(n => n[0]).join("")
                                )}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm">{message.sender}</span>
                                {message.aiGenerated && (
                                  <Badge variant="secondary" className="text-xs gap-1">
                                    <Sparkles className="h-2 w-2" />
                                    AI
                                  </Badge>
                                )}
                                <span className="text-xs text-muted-foreground">
                                  {message.createdAt}
                                </span>
                              </div>
                              <div className={`text-sm p-3 rounded-lg whitespace-pre-line ${
                                message.role === "ai"
                                  ? "bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800"
                                  : "bg-muted"
                              }`}>
                                {message.content}
                              </div>
                              {message.structuredJson && (
                                <div className="mt-2">
                                  {renderAgentCards(message.domain || selectedAgent, message.structuredJson)}
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="mt-2"
                                    disabled={reportingMessageId === message.id}
                                    onClick={() => handleCreateAgentReport(message)}
                                  >
                                    {reportingMessageId === message.id ? (
                                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                    ) : (
                                      <FileText className="h-3 w-3 mr-1" />
                                    )}
                                    {reportedMessageIds.has(message.id) ? 'Recreate Report' : 'Create Report'}
                                  </Button>

                                  {detectMissingData(message.domain || selectedAgent, message.structuredJson).length > 0 && (
                                    <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/30 p-3">
                                      <div className="mb-1.5 text-xs font-semibold text-amber-700">
                                        Add missing details to refine this plan
                                      </div>
                                      <div className="flex flex-wrap gap-1.5">
                                        {detectMissingData(message.domain || selectedAgent, message.structuredJson).map((g) => (
                                          <button
                                            key={g.key}
                                            type="button"
                                            className="rounded-full border bg-card px-3 py-1 text-xs text-muted-foreground hover:bg-muted"
                                            onClick={() => applyMissingPrompt(g.prompt)}
                                          >
                                            {g.label}
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}

                              {!message.structuredJson && (message.domain === "general" || selectedAgent === "general") && message.aiGenerated && (
                                <div className="mt-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="mt-2"
                                    disabled={reportingMessageId === message.id}
                                    onClick={() => handleCreateAgentReport({ ...message, domain: "general" })}
                                  >
                                    {reportingMessageId === message.id ? (
                                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                    ) : (
                                      <FileText className="h-3 w-3 mr-1" />
                                    )}
                                    {reportedMessageIds.has(message.id) ? 'Recreate Report' : 'Create Report'}
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                        {isLoading && (
                          <div className="flex gap-3">
                            <Avatar className="h-8 w-8">
                              <AvatarFallback className="bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300">
                                <Loader2 className="h-4 w-4 animate-spin" />
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm">AI Assistant</span>
                              </div>
                              <div className="text-sm p-3 rounded-lg bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800">
                                <span className="inline-flex items-center gap-2 text-muted-foreground">
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  Processing your query...
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </ScrollArea>

                    {(role?.toLowerCase() === "analyst" || role?.toLowerCase() === "advisor") && isClientSelected && (
                      <div className="mb-3 p-3 bg-primary/5 border border-primary/20 rounded-lg">
                        <div className="flex items-center gap-2 text-sm">
                          <Sparkles className="h-4 w-4 text-primary" />
                          <span>
                            You're chatting with AI on behalf of <strong>{clients.find(c => c.id === selectedClient)?.name}</strong>
                          </span>
                        </div>
                      </div>
                    )}

                    <div className="flex gap-2">
                      <Input
                        ref={chatInputRef}
                        placeholder={
                          (role?.toLowerCase() === "analyst" || role?.toLowerCase() === "advisor") && isClientSelected
                            ? "Ask AI about the selected client's portfolio..."
                            : "Ask AI a question about your portfolio, market insights, or financial planning..."
                        }
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
                      />
                      <Button onClick={handleSendMessage}>
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2">
                      {!isAgentMode ? (
                        <>
                          <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setInputMessage(
                            (role?.toLowerCase() === "analyst" || role?.toLowerCase() === "advisor") && isClientSelected
                              ? "What's the current performance of the selected client's portfolio?"
                              : "What's the current performance of my portfolio?"
                          );
                          setTimeout(handleSendMessage, 100);
                        }}
                      >
                        <Zap className="h-3 w-3 mr-1" />
                        Portfolio Performance
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setInputMessage("Any tax optimization opportunities?");
                          setTimeout(handleSendMessage, 100);
                        }}
                      >
                        <Zap className="h-3 w-3 mr-1" />
                        Tax Strategy
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setInputMessage("Should I rebalance my portfolio?");
                          setTimeout(handleSendMessage, 100);
                        }}
                      >
                        <Zap className="h-3 w-3 mr-1" />
                        Rebalancing Advice
                      </Button>
                        </>
                      ) : (
                        AGENT_PROMPTS[selectedAgent]?.map((prompt) => (
                          <Button
                            key={prompt}
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setInputMessage(prompt);
                              setTimeout(handleSendMessage, 100);
                            }}
                          >
                            <Zap className="h-3 w-3 mr-1" />
                            {prompt}
                          </Button>
                        ))
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Guided Chat Panel */}
            <Card className="min-w-0">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">Guided Research</CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowGuidedChat(!showGuidedChat)}
                  >
                    {showGuidedChat ? 'Hide' : 'Show'}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Select research topics to guide your conversation
                </p>
              </CardHeader>
              {showGuidedChat && (
                <CardContent className="space-y-4">
                  {/* Research Categories */}
                  {!selectedResearchCategory ? (
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-muted-foreground">Research Categories</p>
                      {Object.entries(RESEARCH_HIERARCHY).map(([key, category]) => (
                        <Button
                          key={key}
                          variant="outline"
                          size="sm"
                          className="w-full justify-start text-left"
                          onClick={() => setSelectedResearchCategory(key as keyof typeof RESEARCH_HIERARCHY)}
                          disabled={!isChatSessionActive}
                        >
                          <Sparkles className="h-3 w-3 mr-2" />
                          <span className="text-xs">{key.replace(/_/g, ' ')}</span>
                        </Button>
                      ))}
                    </div>
                  ) : (
                    <>
                      {/* Back Button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedResearchCategory(null);
                          setSelectedAnalysisType(null);
                        }}
                      >
                        ← Back to Categories
                      </Button>

                      {/* Analysis Types */}
                      {!selectedAnalysisType ? (
                        <div className="space-y-2">
                          <p className="text-xs font-semibold text-muted-foreground">
                            {selectedResearchCategory.replace(/_/g, ' ')}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {RESEARCH_HIERARCHY[selectedResearchCategory].description}
                          </p>
                          {Object.entries(RESEARCH_HIERARCHY[selectedResearchCategory].analysisTypes).map(([key, analysisType]) => (
                            <Button
                              key={key}
                              variant="outline"
                              size="sm"
                              className="w-full justify-start text-left"
                              onClick={() => setSelectedAnalysisType(key)}
                              disabled={!isChatSessionActive}
                            >
                              <Search className="h-3 w-3 mr-2" />
                              <span className="text-xs">{key.replace(/_/g, ' ')}</span>
                            </Button>
                          ))}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <p className="text-xs font-semibold text-muted-foreground">
                            {selectedAnalysisType.replace(/_/g, ' ')}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {RESEARCH_HIERARCHY[selectedResearchCategory].analysisTypes[selectedAnalysisType].description}
                          </p>
                          <div className="space-y-1">
                            {RESEARCH_HIERARCHY[selectedResearchCategory].analysisTypes[selectedAnalysisType].defaultQuestionnaires.map((question, index) => (
                              <Button
                                key={index}
                                variant="ghost"
                                size="sm"
                                className="w-full justify-start text-left text-xs h-auto py-2 px-2"
                                onClick={() => {
                                  setInputMessage(question);
                                  setTimeout(handleSendMessage, 100);
                                }}
                                disabled={!isChatSessionActive}
                              >
                                <MessageSquare className="h-3 w-3 mr-2 flex-shrink-0" />
                                <span className="line-clamp-2">{question}</span>
                              </Button>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              )}
            </Card>
          </div>

          {/* Create Report History */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Create Report History</CardTitle>
            </CardHeader>
            <CardContent>
              {reportHistoryLoading ? (
                <div className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading reports...
                </div>
              ) : reportHistory.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No reports generated yet for {isClientSelected ? `this client` : 'you'}. Click "Create Report" on an agent result.
                </p>
              ) : (
                (() => {
                  const byDomain: Record<string, any[]> = {};
                  reportHistory.forEach((r: any) => {
                    const d = r.domain || 'general';
                    byDomain[d] = byDomain[d] || [];
                    byDomain[d].push(r);
                  });
                  return (
                    <div className="space-y-1.5">
                      {Object.entries(byDomain).map(([domain, reports]) => {
                        const isOpen = expandedReportDomains[domain] !== false;
                        return (
                          <div key={domain} className="rounded-lg border">
                            <button
                              type="button"
                              className="flex w-full items-center justify-between gap-2 px-2 py-1.5 text-xs font-medium hover:bg-muted/50"
                              onClick={() => setExpandedReportDomains(prev => ({ ...prev, [domain]: !isOpen }))}
                            >
                              <span>{DOMAIN_LABELS[domain] || domain}</span>
                              <span className="flex items-center gap-1 text-muted-foreground">
                                <span>({reports.length})</span>
                                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isOpen ? '' : '-rotate-90'}`} />
                              </span>
                            </button>
                            {isOpen && (
                              <ul className="space-y-1.5 px-2 pb-2">
                                {reports.slice(0, 5).map((r: any) => (
                                  <li key={r.id} className="flex items-center justify-between rounded-lg border p-2 text-xs">
                                    <div className="min-w-0">
                                      <div className="font-medium">{DOMAIN_LABELS[r.domain] || r.domain}</div>
                                      <div className="text-muted-foreground">
                                        {r.createdAt ? new Date(r.createdAt + (r.createdAt.endsWith('Z') ? '' : 'Z')).toLocaleString() : ''}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                      <Button size="sm" variant="outline" className="text-xs" onClick={() => openReport(r.reportUrl)}>
                                        <FileText className="h-3 w-3 mr-1" /> Open
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-7 w-7 p-0 text-rose-500 hover:text-rose-600"
                                        title="Delete report"
                                        onClick={() => handleDeleteReport(r)}
                                        disabled={deletingReportId === r.id}
                                      >
                                        {deletingReportId === r.id
                                          ? <Loader2 className="h-3 w-3 animate-spin" />
                                          : <Trash2 className="h-3 w-3" />}
                                      </Button>
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>AI Quick Actions</CardTitle>
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] text-muted-foreground uppercase font-semibold px-1 text-right">Default Format</span>
                  <Select value={quickActionFormat} onValueChange={(value: AIStructuredFormat) => setQuickActionFormat(value)}>
                    <SelectTrigger className="w-[150px] h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {AI_STRUCTURED_FORMATS.map((format) => (
                        <SelectItem key={format.value} value={format.value}>
                          {format.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Enable actions to allow AI to process them automatically
              </p>
            </CardHeader>
            <CardContent>
              {(role?.toLowerCase() === "analyst" || role?.toLowerCase() === "advisor") && !isClientSelected ? (
                <div className="flex flex-col items-center justify-center h-[200px] text-center px-4 bg-muted/20 rounded-lg border border-dashed">
                  <UserCircle className="h-10 w-10 text-muted-foreground mb-3" />
                  <h3 className="text-sm font-semibold mb-1">Select a Client for Quick Actions</h3>
                  <p className="text-xs text-muted-foreground max-w-xs">
                    Please select a specific client to view and manage AI-powered quick actions.
                  </p>
                </div>
              ) : (
                <div className="flex flex-wrap gap-4">
                  {combinedQuickActions.map((quickAction) => {
                    const reportData = generatedReports[quickAction.type];
                    const isEnabled = quickActionPrefs[quickAction.type];
                    
                    return (
                      <div key={quickAction.type} className="flex-1 min-w-[200px] max-w-full overflow-hidden p-3 border rounded-lg space-y-3 bg-card">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="shrink-0">{getIconComponent(quickAction.metadata.icon)}</span>
                          <span className="text-sm font-medium">{quickAction.metadata.name}</span>
                          {quickAction.hasApiData && (
                            <Badge variant="outline" className="text-[10px] h-4 shrink-0">
                              API
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 shrink-0">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() => setScheduleAction(quickAction)}
                              title="Schedule"
                            >
                              <Calendar className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() => handleOpenEditDialog(quickAction)}
                              title="Edit Prompt"
                            >
                              <Settings className="h-3 w-3" />
                            </Button>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="text-[10px] text-muted-foreground">{isEnabled ? 'On' : 'Off'}</span>
                            <Switch
                              checked={isEnabled}
                              onCheckedChange={() => toggleQuickAction(quickAction.type)}
                              aria-label={`Enable ${quickAction.metadata.name.toLowerCase()}`}
                            />
                          </div>
                        </div>

                        <div className="flex items-center justify-between gap-2 h-8 min-w-0">
                          <div className="flex items-center gap-2 min-w-0">
                            {isEnabled ? (
                              <>
                                {(!reportData || reportData.status === 'none') && (
                                  <Badge variant="outline" className="text-[10px] h-5">
                                    <Clock className="h-3 w-3 mr-1" />
                                    Not Run
                                  </Badge>
                                )}
                                {reportData?.status === 'processing' && (
                                  <Badge variant="secondary" className="text-[10px] h-5">
                                    <Clock className="h-3 w-3 mr-1 animate-spin" />
                                    Processing
                                  </Badge>
                                )}
                                {reportData?.status === 'ready' && (
                                  <Badge variant="secondary" className="text-[10px] h-5 bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Ready
                                  </Badge>
                                )}
                                {reportData?.status === 'error' && (
                                  <Badge variant="destructive" className="text-[10px] h-5">
                                    <AlertCircle className="h-3 w-3 mr-1" />
                                    Error
                                  </Badge>
                                )}
                              </>
                            ) : (
                              <div className="h-5" /> // Maintain height when hidden
                            )}
                          </div>

                          {reportData?.status === 'ready' && isEnabled && (
                            <Button
                              variant="link"
                              size="sm"
                              className="h-auto p-0 text-[10px] text-blue-600 hover:text-blue-800"
                              onClick={async () => {
                                // Generate presigned URL for download
                                if (quickAction.apiData?.reportUrl) {
                                  try {
                                    const response = await aiService.generateS3PresignedUrl(quickAction.apiData.reportUrl);
                                    const presignedUrl = response.data || response;
                                    if (presignedUrl) {
                                      window.open(presignedUrl, '_blank');
                                    } else {
                                      toast.error("Failed to generate download link");
                                    }
                                  } catch (error) {
                                    console.error('Error generating presigned URL:', error);
                                    toast.error("Failed to generate download link");
                                  }
                                } else {
                                  downloadReport(quickAction.type);
                                }
                              }}
                            >
                              <Download className="h-3 w-3 mr-1" />
                              PDF
                            </Button>
                          )}
                        </div>

                        {quickAction.hasApiData && quickAction.apiData?.status && (
                          <div className="space-y-0.5 text-[10px] text-muted-foreground min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="shrink-0 font-medium">Last Run:</span>
                              <span className="truncate capitalize">{quickAction.apiData?.triggerType || '—'}</span>
                            </div>
                            <div className="flex items-center justify-between gap-2">
                              <span className="shrink-0 font-medium">Run Date:</span>
                              <span className="truncate">{formatDateTime(quickAction.apiData?.completedAt)}</span>
                            </div>
                          </div>
                        )}

                        {isEnabled && (
                          <div className="pt-1">
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-full h-7 text-[10px]"
                              onClick={() => handleRerunAction(quickAction.type)}
                              disabled={reportData?.status === 'processing' || ((role?.toLowerCase() === "analyst" || role?.toLowerCase() === "advisor") && !isClientSelected)}
                            >
                              <Zap className="h-3 w-3 mr-1" />
                              Rerun
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {scheduleAction && (
            <QuickActionScheduleDialog
              open={!!scheduleAction}
              onOpenChange={(open) => { if (!open) setScheduleAction(null); }}
              actionType={scheduleAction.type}
              actionName={scheduleAction.metadata.name}
              defaultPrompt={scheduleAction.metadata.prompt}
              userId={localStorage.getItem('userId') || ''}
              clientId={effectiveClientId || null}
            />
          )}

          {intakeOpen && (
            <FinancialIntakeDialog
              open={intakeOpen}
              onOpenChange={setIntakeOpen}
              clientId={effectiveClientId || (localStorage.getItem('userId') || '')}
              clientName={isClientSelected ? clients.find((c) => c.id === selectedClient)?.name : undefined}
            />
          )}
        </TabsContent>

        {/* AI Insights Tab */}
        <TabsContent value="insights" className="space-y-4">
          {(role?.toLowerCase() === "analyst" || role?.toLowerCase() === "advisor") && isClientSelected && (
            <Card className="border-primary/20 bg-primary/5">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-sm">
                  <UserCircle className="h-4 w-4" />
                  <span>Showing AI insights for</span>
                  <Badge variant="secondary">
                    {clients.find(c => c.id === selectedClient)?.name}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {aiInsights.map((insight) => (
              <Card
                key={insight.id}
                className={
                  insight.type === "alert"
                    ? "border-orange-200 dark:border-orange-800"
                    : insight.type === "opportunity"
                    ? "border-green-200 dark:border-green-800"
                    : ""
                }
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div
                        className={`p-2 rounded-full ${
                          insight.type === "recommendation"
                            ? "bg-blue-100 dark:bg-blue-900"
                            : insight.type === "alert"
                            ? "bg-orange-100 dark:bg-orange-900"
                            : insight.type === "opportunity"
                            ? "bg-green-100 dark:bg-green-900"
                            : "bg-purple-100 dark:bg-purple-900"
                        }`}
                      >
                        {insight.type === "recommendation" ? (
                          <Sparkles className="h-4 w-4 text-blue-600 dark:text-blue-300" />
                        ) : insight.type === "alert" ? (
                          <AlertCircle className="h-4 w-4 text-orange-600 dark:text-orange-300" />
                        ) : insight.type === "opportunity" ? (
                          <TrendingUp className="h-4 w-4 text-green-600 dark:text-green-300" />
                        ) : (
                          <BarChart3 className="h-4 w-4 text-purple-600 dark:text-purple-300" />
                        )}
                      </div>
                      <div>
                        <CardTitle className="text-base">{insight.title}</CardTitle>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-xs text-muted-foreground">
                            {formatDate(insight.createdAt)}
                          </p>
                          {insight.aiSuggested && (
                            <Badge variant="secondary" className="text-[10px] h-4 py-0 px-1 gap-1">
                              <Sparkles className="h-2 w-2" />
                              AI
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <Badge
                      variant="secondary"
                      className={
                        insight.confidence > 90
                          ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
                          : "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
                      }
                    >
                      {(insight.confidence * 100).toFixed(0)}% confident
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">
                    {insight.description}
                  </p>
                  
                  {expandedInsights[insight.id] && insight.details && (
                    <div className="mb-4 p-3 bg-muted rounded-lg border border-border animate-in fade-in slide-in-from-top-1 duration-200">
                      <p className="text-sm">{insight.details}</p>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="flex gap-1">
                      {insight.relevantTo.map((role) => (
                        <Badge key={role} variant="outline" className="text-xs">
                          {role}
                        </Badge>
                      ))}
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => toggleInsightDetails(insight.id)}
                    >
                      {expandedInsights[insight.id] ? "Close" : "View Details"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* AI Tasks Tab */}
        <TabsContent value="tasks" className="space-y-4">
          {(role?.toLowerCase() === "analyst" || role?.toLowerCase() === "advisor") && isClientSelected && (
            <Card className="border-primary/20 bg-primary/5">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-sm">
                  <UserCircle className="h-4 w-4" />
                  <span>Managing tasks for</span>
                  <Badge variant="secondary">
                    {clients.find(c => c.id === selectedClient)?.name}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          )}
          <Card>
            <CardHeader>
              <CardTitle>AI-Suggested Tasks</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {tasks.map((task) => (
                  <div
                    key={task.id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="flex items-start gap-3 flex-1">
                      <div
                        className={`p-2 rounded-full ${
                          task.status === "completed"
                            ? "bg-green-100 dark:bg-green-900"
                            : task.status === "in-progress"
                            ? "bg-blue-100 dark:bg-blue-900"
                            : "bg-gray-100 dark:bg-gray-900"
                        }`}
                      >
                        {task.status === "completed" ? (
                          <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-300" />
                        ) : task.status === "in-progress" ? (
                          <Clock className="h-4 w-4 text-blue-600 dark:text-blue-300" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex flex-col gap-1 mb-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium">{task.title}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <p className="text-xs text-muted-foreground">
                              {formatDate(task.createdAt)}
                            </p>
                            {task.aiSuggested && (
                              <Badge variant="secondary" className="text-[10px] h-4 py-0 px-1 gap-1">
                                <Sparkles className="h-2 w-2" />
                                AI
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">
                            {task.assignedTo}
                          </span>
                          <Badge
                            variant="outline"
                            className={`text-xs ${
                              task.priority === "high"
                                ? "border-red-500 text-red-500"
                                : task.priority === "medium"
                                ? "border-orange-500 text-orange-500"
                                : "border-gray-500 text-gray-500"
                            }`}
                          >
                            {task.priority}
                          </Badge>
                          <Badge
                            variant="outline"
                            className={`text-xs ${
                              task.status === "completed"
                                ? "border-green-500 text-green-600"
                                : task.status === "in-progress"
                                ? "border-blue-500 text-blue-600"
                                : task.status === "cancelled"
                                ? "border-red-400 text-red-500"
                                : "border-gray-400 text-gray-500"
                            }`}
                          >
                            {task.status === "completed"
                              ? "Completed"
                              : task.status === "in-progress"
                              ? "In Progress"
                              : task.status === "cancelled"
                              ? "Cancelled"
                              : "Pending"}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {task.status === "completed" ? (
                        <Button size="sm" variant="outline" disabled>
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Done
                        </Button>
                      ) : task.status === "in-progress" || task.status === "cancelled" ? (
                        <span className="text-xs text-muted-foreground italic">—</span>
                      ) : confirmingTaskId === task.id ? (
                        <div className="flex flex-col items-end gap-2">
                          <textarea
                            className="w-48 text-xs border rounded-md p-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-primary"
                            rows={2}
                            placeholder="Optional comment..."
                            value={taskComment}
                            onChange={e => setTaskComment(e.target.value)}
                            autoFocus
                          />
                          <div className="flex gap-1.5">
                            <Button
                              size="sm"
                              variant={confirmAction === 'complete' ? 'default' : 'outline'}
                              className={confirmAction === 'complete' ? 'bg-green-600 hover:bg-green-700' : ''}
                              onClick={handleConfirmTask}
                            >
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Confirm
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={handleCancelConfirm}
                            >
                              Back
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            variant="default"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => { setConfirmingTaskId(task.id); setConfirmAction('complete'); setTaskComment(''); }}
                          >
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Complete
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => { setConfirmingTaskId(task.id); setConfirmAction('cancel'); setTaskComment(''); }}
                          >
                            <XCircle className="h-3 w-3 mr-1" />
                            Cancel
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Analytics Tab */}
        <TabsContent value="analytics" className="space-y-4">
          {(role?.toLowerCase() === "analyst" || role?.toLowerCase() === "advisor") && isClientSelected && (
            <Card className="border-primary/20 bg-primary/5">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-sm">
                  <UserCircle className="h-4 w-4" />
                  <span>Analytics for</span>
                  <Badge variant="secondary">
                    {clients.find(c => c.id === selectedClient)?.name}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Team Engagement</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Messages Sent</span>
                      <span>247</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-blue-500" style={{ width: "85%" }} />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>AI Interactions</span>
                      <span>89</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-purple-500" style={{ width: "65%" }} />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Tasks Completed</span>
                      <span>34</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-green-500" style={{ width: "90%" }} />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">AI Accuracy</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center">
                  <div className="text-4xl mb-2">94%</div>
                  <p className="text-sm text-muted-foreground">
                    Recommendation acceptance rate
                  </p>
                  <div className="mt-4 space-y-2">
                    <div className="flex justify-between text-xs">
                      <span>Predictions</span>
                      <span className="text-green-600">96%</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>Insights</span>
                      <span className="text-green-600">92%</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>Recommendations</span>
                      <span className="text-green-600">94%</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Time Saved</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center">
                  <div className="text-4xl mb-2">12.5 hrs</div>
                  <p className="text-sm text-muted-foreground">
                    This week through AI automation
                  </p>
                  <div className="mt-4 space-y-2 text-xs">
                    <div className="flex justify-between">
                      <span>Report Generation</span>
                      <span>4.2 hrs</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Data Analysis</span>
                      <span>5.1 hrs</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Meeting Summaries</span>
                      <span>3.2 hrs</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Recent AI Activities</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-start gap-3 pb-3 border-b">
                  <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900">
                    <Sparkles className="h-4 w-4 text-blue-600 dark:text-blue-300" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm">Generated portfolio rebalancing proposal</p>
                    <p className="text-xs text-muted-foreground">15 minutes ago</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 pb-3 border-b">
                  <div className="p-2 rounded-full bg-green-100 dark:bg-green-900">
                    <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-300" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm">Completed market sentiment analysis</p>
                    <p className="text-xs text-muted-foreground">1 hour ago</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 pb-3 border-b">
                  <div className="p-2 rounded-full bg-purple-100 dark:bg-purple-900">
                    <Brain className="h-4 w-4 text-purple-600 dark:text-purple-300" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm">Identified 3 tax optimization opportunities</p>
                    <p className="text-xs text-muted-foreground">3 hours ago</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-full bg-orange-100 dark:bg-orange-900">
                    <AlertCircle className="h-4 w-4 text-orange-600 dark:text-orange-300" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm">Sent volatility alert to team members</p>
                    <p className="text-xs text-muted-foreground">5 hours ago</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
          </Tabs>
        </TabsContent>

        {/* Document Research Tab */}
        <TabsContent value="document-research" className="space-y-4">
          {(role?.toLowerCase() === "analyst" || role?.toLowerCase() === "advisor") && isClientSelected && (
            <Card className="border-primary/20 bg-primary/5">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-sm">
                  <FileText className="h-4 w-4" />
                  <span>Document research for</span>
                  <Badge variant="secondary">
                    {clients.find(c => c.id === selectedClient)?.name}
                  </Badge>
                  <span className="text-muted-foreground">•</span>
                  <span className="text-muted-foreground">Upload and analyze client-specific documents</span>
                </div>
              </CardContent>
            </Card>
          )}
          <Tabs defaultValue="documents" className="w-full">
            <TabsList className="grid w-full grid-cols-2 max-w-md mx-auto">
              <TabsTrigger value="documents">
                <Upload className="h-4 w-4 mr-2" />
                Documents
              </TabsTrigger>
              <TabsTrigger value="research">
                <Search className="h-4 w-4 mr-2" />
                Research
              </TabsTrigger>
            </TabsList>

            {/* Documents Upload Tab */}
            <TabsContent value="documents" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Document Upload</CardTitle>
                <Button>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Files
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Upload Area */}
              <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 mb-6 text-center hover:border-primary/50 transition-colors cursor-pointer">
                <div className="flex flex-col items-center gap-3">
                  <div className="p-3 rounded-full bg-primary/10">
                    <Upload className="h-8 w-8 text-primary" />
                  </div>
                  <div>
                    <p className="mb-1">
                      Drag and drop files here, or click to browse
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Supports PDF, DOCX, XLSX, CSV (Max 10MB per file)
                    </p>
                  </div>
                  <Button variant="outline" size="sm">
                    Select Files
                  </Button>
                </div>
              </div>

              {/* Uploaded Documents List */}
              <div className="space-y-3">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm">Uploaded Documents ({uploadedDocuments.length})</h3>
                  <Select defaultValue="all">
                    <SelectTrigger className="w-[150px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Documents</SelectItem>
                      <SelectItem value="ready">Ready</SelectItem>
                      <SelectItem value="processing">Processing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {uploadedDocuments.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <div className="p-2 rounded bg-primary/10">
                        <File className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="truncate">{doc.name}</p>
                          <Badge
                            variant={
                              doc.status === "ready"
                                ? "secondary"
                                : doc.status === "processing"
                                ? "outline"
                                : "destructive"
                            }
                            className="shrink-0"
                          >
                            {doc.status === "processing" && (
                              <Clock className="h-3 w-3 mr-1" />
                            )}
                            {doc.status === "ready" && (
                              <CheckCircle className="h-3 w-3 mr-1" />
                            )}
                            {doc.status}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span>{doc.type}</span>
                          <span>•</span>
                          <span>{doc.size}</span>
                          <span>•</span>
                          <span>Uploaded by {doc.uploadedBy}</span>
                          <span>•</span>
                          <span>{doc.uploadDate}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {doc.status === "ready" && (
                        <>
                          <Button variant="ghost" size="sm">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm">
                            <Download className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      <Button variant="ghost" size="sm">
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* AI Processing Status */}
          <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950 dark:border-blue-800">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900">
                  <Brain className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="mb-1">AI Document Processing</h3>
                  <p className="text-sm text-muted-foreground">
                    Your documents are automatically analyzed by AI to extract insights,
                    generate summaries, and identify key investment opportunities. Switch to
                    the Research sub-tab to view detailed analysis.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Document Research Tab */}
        <TabsContent value="research" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>AI Document Research</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    AI-powered analysis of your uploaded documents
                  </p>
                </div>
                <Select defaultValue="all">
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Research</SelectItem>
                    <SelectItem value="summary">Summaries</SelectItem>
                    <SelectItem value="insights">Insights</SelectItem>
                    <SelectItem value="recommendations">Recommendations</SelectItem>
                    <SelectItem value="risk-analysis">Risk Analysis</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {documentResearch.map((research) => (
                  <Card key={research.id} className="border-2">
                    <CardHeader>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge
                              variant="outline"
                              className={
                                research.type === "summary"
                                  ? "border-blue-500 text-blue-600"
                                  : research.type === "insights"
                                  ? "border-purple-500 text-purple-600"
                                  : research.type === "recommendations"
                                  ? "border-green-500 text-green-600"
                                  : "border-orange-500 text-orange-600"
                              }
                            >
                              {research.type === "summary" && <FileText className="h-3 w-3 mr-1" />}
                              {research.type === "insights" && <Sparkles className="h-3 w-3 mr-1" />}
                              {research.type === "recommendations" && <Target className="h-3 w-3 mr-1" />}
                              {research.type === "risk-analysis" && <AlertCircle className="h-3 w-3 mr-1" />}
                              {research.type.replace("-", " ")}
                            </Badge>
                            <Badge variant="secondary" className="gap-1">
                              <Zap className="h-3 w-3" />
                              {research.confidence}% confidence
                            </Badge>
                          </div>
                          <CardTitle className="text-base mb-1">{research.title}</CardTitle>
                          <p className="text-xs text-muted-foreground">
                            From: {research.documentName} • {research.createdAt}
                          </p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="text-sm whitespace-pre-line leading-relaxed">
                        {research.content}
                      </div>
                      <div className="flex items-center gap-2 mt-4 pt-4 border-t">
                        <Button variant="outline" size="sm">
                          <FileText className="h-4 w-4 mr-2" />
                          View Source
                        </Button>
                        <Button variant="outline" size="sm">
                          <MessageSquare className="h-4 w-4 mr-2" />
                          Discuss with AI
                        </Button>
                        <Button variant="outline" size="sm">
                          <Users className="h-4 w-4 mr-2" />
                          Share with Team
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Research Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Documents Analyzed</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl">12</div>
                <p className="text-xs text-muted-foreground">Last 30 days</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Insights Generated</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl">48</div>
                <p className="text-xs text-muted-foreground">
                  <span className="text-green-600">+12%</span> vs last month
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Avg Confidence</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl">91%</div>
                <p className="text-xs text-muted-foreground">Across all research</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Time Saved</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl">24h</div>
                <p className="text-xs text-muted-foreground">Manual analysis time</p>
              </CardContent>
            </Card>
          </div>
            </TabsContent>
          </Tabs>
        </TabsContent>
      </Tabs>

      {/* Edit Quick Action Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Edit Quick Action: {editActionName}</DialogTitle>
            <DialogDescription>
              Customize the prompt used by AI for this quick action. This allows you to tailor the output to your specific needs.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="prompt">AI Prompt</Label>
              <Textarea
                id="prompt"
                placeholder="Enter the prompt for the AI..."
                className="h-[300px] text-sm"
                value={editPrompt}
                onChange={(e) => setEditPrompt(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                The prompt instructs the AI on how to analyze data and generate the report.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)} disabled={isSavingAction}>
              Cancel
            </Button>
            <Button onClick={handleSaveQuickAction} disabled={isSavingAction}>
              {isSavingAction ? (
                <>
                  <Clock className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
